import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'

// Components
import ConfirmationModal from '../confirmation-modal'
import Link from '../link'

// Constants
import {GIFTCARD_HREF} from '@app/constants'

// Hooks
import {useCurrentBasket} from '@app/hooks/use-current-basket'
import {useDisclosure} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useGiftCardActions} from '@app/hooks/use-gift-card-actions'
import {useGiftCardLogic} from '@app/hooks/use-gift-card-logic'
import {useIntl} from 'react-intl'
import useShipToCountry from '@app/hooks/use-ship-to-country'

// Utils
import {defineMessages} from 'react-intl'

const GIFT_CARD_UNAVAILABLE_DIALOG_CONFIG = defineMessages({
    dialogTitle: {
        defaultMessage: 'Online Gift Cards not available',
        id: 'giftcard.unavailable_gift_cards_modal.title'
    },
    primaryActionLabel: {
        defaultMessage: 'Ok',
        id: 'confirmation_modal.remove_cart_item.action.primary.label'
    },
    primaryActionAriaLabel: {
        defaultMessage: 'Ok',
        id: 'confirmation_modal.remove_cart_item.action.primary.aria'
    },
    countryNotSupportedMessage: {
        defaultMessage:
            'The gift certificate isn’t handled in the country you’ve chosen and has been removed from your cart. If you still want to buy a gift certificate, please choose Euro or GBP currency and make your whole order in Euro or GBP.',
        id: 'giftcard.unavailable_gift_cards_modal.message.country_not_supported'
    }
})

/**
 * Displays a modal dialog to inform users that a selected gift card is unavailable.
 *
 * @component
 * @param {boolean} [isCartPage=false] - Indicates whether the modal is being displayed on the cart page.
 * @returns {JSX.Element} The rendered modal component.
 */
const UnavailableGiftCardModal = (isCartPage = false) => {
    const modalDisclosure = useDisclosure()
    const {formatMessage} = useIntl()
    const {
        data: basket,
        isLoading: isBasketLoading,
        isFetchedAfterMount: isBasketFetchedAfterMount
    } = useCurrentBasket()
    const {
        isGCAvailableForCurrentCountry,
        isGCAvailableForCurrency,
        checkGiftCardCurrencyMismatch
    } = useGiftCardLogic()
    const {shipToCountry} = useShipToCountry()
    const {removeAllGiftCardLineItems} = useGiftCardActions(basket)

    const CURRENCY_MISMATCH_MESSAGE = formatMessage(
        {
            defaultMessage:
                'The amount of your gift certificate could not be updated and has been removed from your cart. If you still want to buy a gift certificate, please go back to the <a>gift certificate page</a> and add a new one.',
            id: 'giftcard.unavailable_gift_cards_modal.message.currency_mismatch'
        },
        {
            a: (chunks) => <Link to={GIFTCARD_HREF}>{chunks}</Link>
        }
    )

    const currency = basket?.c_paymentCurrencyCode || basket?.currency
    const giftCardLineItems = basket?.giftCertificateItems ?? []
    const isCountrySupported = isGCAvailableForCurrentCountry(shipToCountry.id)
    const isCurrencySupported = isGCAvailableForCurrency(currency)
    const isBuyingGiftCardsSupported = isCurrencySupported && isCountrySupported

    const isCurrencyMismatch =
        giftCardLineItems?.length > 0 && checkGiftCardCurrencyMismatch(giftCardLineItems, currency)
    const showUnsupportedGiftCardModal =
        isBasketFetchedAfterMount &&
        giftCardLineItems?.length > 0 &&
        (!isBuyingGiftCardsSupported || checkGiftCardCurrencyMismatch(giftCardLineItems, currency))

    const {countryNotSupportedMessage, ...modalMessages} = GIFT_CARD_UNAVAILABLE_DIALOG_CONFIG

    const [modalMessage, setModalMessage] = useState(countryNotSupportedMessage)

    useEffect(() => {
        if (showUnsupportedGiftCardModal) {
            setModalMessage(
                isBuyingGiftCardsSupported ? CURRENCY_MISMATCH_MESSAGE : countryNotSupportedMessage
            )

            modalDisclosure.onOpen()
            handleUnsupportedGiftCards()
        }
    }, [showUnsupportedGiftCardModal])

    /**
     * Handles currency mismatches for gift certificates in the basket.
     */
    function handleUnsupportedGiftCards() {
        if (!basket?.basketId || isBasketLoading) return

        // Remove gift certificate line items from basket
        if (!isBuyingGiftCardsSupported || isCurrencyMismatch) {
            if (giftCardLineItems?.length > 0) {
                removeAllGiftCardLineItems(isCartPage)
            }
        }
    }

    return (
        <ConfirmationModal
            {...modalMessages}
            {...modalDisclosure}
            data-testid="unavailable-gift-cards-modal"
            closeOnEsc={false}
            closeOnOverlayClick={false}
            hideAlternateAction={true}
            confirmationMessage={modalMessage}
        />
    )
}

UnavailableGiftCardModal.displayName = 'UnavailableGiftCardModal'
UnavailableGiftCardModal.propTypes = {
    isCartPage: PropTypes.bool
}
export default UnavailableGiftCardModal
